import * as React from 'react'
import { Animated, Linking, Text, TouchableOpacity, View } from 'react-native'
import styled from '@theme/styled-components'

import Icon from '@components/icon/Icons'
import DocumentButton, { BUTTON_HEIGHT } from '@components/button/DocumentButton'
import AlertButton from '@components/button/AlertButton'

import useBreakpoint from '@utils/useBreakpoints'
import useI18n from '@i18n/useI18n'
import useNavigation from '@layout/useNavigation'

import { manifestationDate } from '../manifestationsUtils'
import { isTomorrow } from 'date-fns'

import theme from '@theme/Theme'

const MAIN_VERTICAL_PADDING = 16

interface Props {
  hasScrolled: boolean
  onBack?: () => void
  onMapPressed: () => void
  manifestation: ManifestationDetailResponse
  nextActionOnManifestationDate?: Date
  edlList: EtatDesLieuxResponse[]
  controles: ControleQualiteResponse[]
}

const ANIM_DURATION = 500
const SMALL_HEADER_HEIGHT = 65

const ManifestationHeader = ({
  hasScrolled,
  onBack,
  onMapPressed,
  manifestation,
  nextActionOnManifestationDate,
  edlList,
  controles,
}: Props) => {
  const i18n = useI18n()
  const navigation = useNavigation()
  const [{ width }] = useBreakpoint()
  const [initHeight, setInitHeight] = React.useState(0)

  const nameOpacityAnim = React.useRef(new Animated.Value(0)).current
  const contentOpacityAnim = React.useRef(new Animated.Value(1)).current
  const heightAnim = React.useRef(new Animated.Value(0)).current

  React.useEffect(() => {
    // Small manifestation name appears
    Animated.parallel([
      Animated.timing(nameOpacityAnim, {
        toValue: hasScrolled ? 1 : 0,
        duration: ANIM_DURATION,
        useNativeDriver: true,
      }),
      Animated.timing(contentOpacityAnim, {
        toValue: hasScrolled ? 0 : 1,
        duration: ANIM_DURATION / 2,
        useNativeDriver: true,
      }),
      Animated.timing(heightAnim, {
        toValue: hasScrolled ? SMALL_HEADER_HEIGHT : initHeight,
        duration: ANIM_DURATION,
        useNativeDriver: false,
      }),
    ]).start()
  }, [hasScrolled, initHeight])

  const onDocuments = () => {
    // TODO : not implemented
    navigation.push('/documents', {
      documents: {
        edl: {},
        quality: controles,
      },
      isManifestation: true,
      id: manifestation.id,
      edlList: edlList,
    })
  }

  const onEmail = (email: string) => {
    Linking.openURL(`mailto:${email}`)
  }

  const onPhone = (phone?: string) => {
    if (!!phone) {
      Linking.openURL(`tel:${phone}`)
    }
  }

  const displayActionButton = !!nextActionOnManifestationDate
  const fadeAndScaleStyle: any = {
    opacity: contentOpacityAnim,
    transform: [{ scaleY: contentOpacityAnim }],
  }

  const renderContact = (contact: ChefProjetManifestationResponse) => {
    return (
      <Contact key={contact.code}>
        <ContactInfo>
          <ContactName>
            {contact.prenom} {contact.nom}
          </ContactName>
        </ContactInfo>
        {!!contact.email && (
          <ContactButton activeOpacity={0.9} onPress={() => onEmail(contact.email)}>
            <Icon name="email" size={24} color={theme.colors.background} />
          </ContactButton>
        )}
        {!!contact.telephone && (
          <ContactButton activeOpacity={0.9} onPress={() => onPhone(contact.telephone)}>
            <Icon name="phone" size={24} color={theme.colors.background} />
          </ContactButton>
        )}
      </Contact>
    )
  }

  return (
    <GlobalContainer hasScrolled={hasScrolled} displayActionButton={displayActionButton}>
      <SafeContainer hasScrolled={hasScrolled}>
        <Container
          hasScrolled={hasScrolled}
          style={initHeight > 0 ? { height: heightAnim } : undefined}
          onLayout={(e) => {
            if (initHeight === 0) {
              heightAnim.setValue(e.nativeEvent.layout.height)
              setInitHeight(e.nativeEvent.layout.height)
            }
          }}>
          <TopContainer>
            <BackButton activeOpacity={0.9} onPress={onBack}>
              <Icon name="arrow-left" size={35} color={theme.colors.background} />
            </BackButton>
            <SmallName numberOfLines={1} ellipsizeMode="tail" style={{ opacity: nameOpacityAnim }}>
              {manifestation.libelle}
            </SmallName>
            {/* TODO : conditionner à la présence d'une map */}
            {true ? (
              <MapButton activeOpacity={0.9} onPress={onMapPressed}>
                <Icon name="map" size={20} color={theme.colors.primary} />
              </MapButton>
            ) : (
              <DummyView size={35} />
            )}
          </TopContainer>

          <Content style={fadeAndScaleStyle}>
            <Name numberOfLines={2} ellipsizeMode="tail">
              {manifestation.libelle}
            </Name>
            <ManifestationDate>{manifestationDate(manifestation, i18n)}</ManifestationDate>
            {manifestation.chefProjetManifestation.length !== 0 && (
              <>
                <Separator />
                <ContactRole>
                  {i18n.t('screens.manifestation.role', { count: manifestation.chefProjetManifestation.length })}
                </ContactRole>

                {manifestation.chefProjetManifestation.map(renderContact)}
              </>
            )}
          </Content>
        </Container>
      </SafeContainer>
      <AlertButtonContainer hasScrolled={hasScrolled} style={{ ...fadeAndScaleStyle, width }}>
        {displayActionButton && (
          <ButtonBackground isTop>
            <AlertButton
              label={
                isTomorrow(nextActionOnManifestationDate)
                  ? i18n.t('screens.site.actionsTomorrow')
                  : i18n.t('screens.site.actionsDate', { date: nextActionOnManifestationDate })
              }
              onClick={() => undefined}
            />
          </ButtonBackground>
        )}
        <ButtonBackground>
          <DocumentButton count={edlList.length + controles.length} onClick={onDocuments} />
        </ButtonBackground>
      </AlertButtonContainer>
    </GlobalContainer>
  )
}

export default ManifestationHeader
const GlobalContainer = styled(View)<{ hasScrolled: boolean; displayActionButton: boolean }>`
  ${(props) =>
    !props.hasScrolled &&
    `padding-bottom: ${props.displayActionButton ? (3 * BUTTON_HEIGHT) / 2 : BUTTON_HEIGHT / 2}px;`}
`
const SafeContainer = styled(View)<{ hasScrolled: boolean }>`
  z-index: 2;
  background-color: ${(props) => props.theme.colors.primary};
  padding-top: ${(props) => props.theme.insets.top}px;
`
const Container = styled(Animated.View)<{ hasScrolled: boolean }>`
  z-index: 2;
  background-color: ${(props) => props.theme.colors.primary};
  ${(props) => !props.hasScrolled && `padding-bottom: ${BUTTON_HEIGHT / 2 + MAIN_VERTICAL_PADDING}px;`}
`
const TopContainer = styled(View)`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: ${MAIN_VERTICAL_PADDING}px;
`
const BackButton = styled(TouchableOpacity)`
  flex-direction: row;
  align-items: center;
`
const Name = styled(Text)`
  ${(props) => props.theme.fonts.Title1};
  color: ${(props) => props.theme.colors.background};
  text-align: center;
  padding: 7px 0;
`
const ManifestationDate = styled(Text)`
  ${(props) => props.theme.fonts.Caption1};
  color: ${(props) => props.theme.colors.background};
  text-align: center;
  margin-bottom: 16px;
`
const AlertButtonContainer = styled(Animated.View)<{ hasScrolled: boolean }>`
  z-index: ${(props) => (props.hasScrolled ? 0 : 3)};
  padding: ${MAIN_VERTICAL_PADDING}px;
  padding-bottom: 0;
  position: absolute;
  left: 0;
  bottom: 0;
`
const ButtonBackground = styled(View)<{ isTop?: boolean }>`
  border-radius: 10px;
  background-color: ${(props) => props.theme.colors.primary};
  ${(props) => props.isTop && `margin-bottom: 8px;`}
`
const Separator = styled(View)`
  height: 1px;
  background-color: ${(props) => props.theme.colors.disable};
  margin-bottom: 16px;
`
const Content = styled(Animated.View)`
  justify-content: center;
  padding: 0 ${MAIN_VERTICAL_PADDING}px;
`
const Contact = styled(View)`
  flex-direction: row;
  align-items: center;
`
const ContactName = styled(Text)`
  ${(props) => props.theme.fonts.Subheadline};
  color: ${(props) => props.theme.colors.background};
`
const ContactButton = styled(TouchableOpacity)`
  padding: 0 8px;
`
const ContactRole = styled(Text)`
  ${(props) => props.theme.fonts.Caption1};
  color: ${(props) => props.theme.colors.background};
`
const ContactInfo = styled(View)`
  flex: 1;
`
const MapButton = styled(TouchableOpacity)`
  height: 32px;
  width: 32px;
  border-radius: 36px;
  align-items: center;
  justify-content: center;
  background-color: ${(props) => props.theme.colors.background};
`
const SmallName = styled(Animated.Text)`
  ${(props) => props.theme.fonts.Headline};
  color: ${(props) => props.theme.colors.background};
  text-align: center;
  max-width: 250px;
`
const DummyView = styled(View)<{ size: number }>`
  height: ${(props) => props.size}px;
  width: ${(props) => props.size}px;
`
